import Type from "./type";
import uniqueId from "@/util/uniqueId";

class Graph {
    constructor() {
        this._types = []
        this.defaultRootName = ""
    };

    applySchema(schema) {
        this.rootName = schema.rootName;
        this.pathEntityTypes = schema.entityTypes;

        if (!this.defaultRootName) {
            this.defaultRootName = this.rootName
        }

        if (!this._rootType) {
            this._rootType = schema.types.find(t => t.name === schema.rootName);
        }

        schema.types.forEach(t => t.rootType = this.rootType);

        const store = {};

        schema.types.forEach(type => {
            if ((type.kind === "object" || type.kind === "instance") && type.name !== schema.rootName) {
                store[type.name] = `${type.name}${uniqueId()}`;
            }
        });

        schema.types.forEach(type => {
            if (store[type.name]) {
                type.name = store[type.name];
            }

            type.inheritedTypes = type.inheritedTypes.map(inheritedType => store[inheritedType] ? store[inheritedType] : inheritedType);

            type.fields.forEach(field => {
                let fieldType = field.type;
                while (fieldType.kind === "list") {
                    fieldType = field.type.ofType;
                }

                if (store[fieldType.name]) {
                    fieldType.name = store[fieldType.name];
                }
            });

            this._types.push(type);
        });
    }

    resetRootName() {
        this.rootName = this.defaultRootName;
    }

    getRoot() {
        return new Type(this._rootType, this);
    }

    getType(name) {
        const type = this._types.find(t => t.name === name);
        if (type) {
            return new Type(type, this);
        }

        return null;
    }

    getTypePath(type, field) {
        let typeValue = type.value;
        let path = { path: null, queryResultFields: [] };
        let items = [];
        let queryString = "";

        if (field) {
            if (field.value.queryString) {
                queryString = field.value.queryString;
            } else {
                items.push(field.value.name);
                path.field = field.value;
            }
        }

        while (typeValue) {
            if (typeValue.container) {
                path.path = items.join(".");
                const next = {
                    path: null,
                    include: path,
                    queryResultFields: []
                };

                path = next;
                items = [];
                typeValue = typeValue.container;

                continue;
            }

            if (typeValue.field) {
                if (typeValue.field.queryString) {
                    queryString = typeValue.field.queryString + queryString;

                    path.queryResultFields.splice(0, 0, typeValue.field);
                } else {
                    let name = typeValue.field.name;
                    if (queryString) {
                        name += queryString;
                        queryString = "";
                    }

                    items.splice(0, 0, name);

                    if (!path.field) {
                        path.field = typeValue.field
                    };
                }
            }

            typeValue = typeValue.parent;
        }

        path.path = items.join(".");
        return path;
    }

    getInnerMostTypePath(path) {
        while (path.include) {
            if (!path.include) {
                break;
            }

            path = path.include;
        }

        return path;
    }

    static getPathString(path) {
        return path.reduce((pathStr, field) => {
            if (field.isResult()) {
                pathStr = `${pathStr}${field.queryString}`;
            } else {
                pathStr = `${pathStr}${!!pathStr ? "." : ""}${field.value.name}`;
            }

            return pathStr;
        }, "");
    }

    buildPath(pathString) {
        const path = [];
        if (pathString) {
            const pathParts = pathString.split(".");
            for (let i = 0; i < pathParts.length; i++) {
                let type;
                if (path.length > 0) {
                    const lastField = path[path.length - 1];
                    type = lastField.getUnderlyingType();
                } else {
                    type = this.getRoot();
                }

                const match = /^(?<name>\w+)(?<queryString>\!\S+)?$/g.exec(pathParts[i]);
                if (match) {
                    const field = type.getFields().find(f => f.value.name === match.groups.name);
                    if (field) {
                        path.push(field);
                        if (match.groups.queryString) {
                            const qs = `${Graph.getPathString(path)}${match.groups.queryString}`;
                            const queryField = this.getType(qs).getFields().find(f => f.value.name === "__result");
                            if (queryField) {
                                path.push(queryField);
                            }
                        }
                    }
                }
            }
        }

        return path;
    }
}

export default Graph;